import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosInstance from "../../../hooks/api";

const BRAND = "#032C65";
const ACCENT = "#E63946";

const statusStyles = {
  pending: { bg: "#fff8e6", color: "#8a6d1d", border: "#f0d78c" },
  paid: { bg: "#e8f7ee", color: "#1b6b3a", border: "#a8dbb8" },
  expired: { bg: "#fdecea", color: "#8a1f1f", border: "#f0b4b4" },
  void: { bg: "#f4f4f4", color: "#555", border: "#ddd" },
  refunded: { bg: "#fdecea", color: "#8a1f1f", border: "#f0b4b4" },
  partially_refunded: { bg: "#fff8e6", color: "#8a6d1d", border: "#f0d78c" },
};

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const PaymentForm = ({ token, t, onError, billingName, billingEmail, billingPhone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    onError(null);

    try {
      const returnUrl = `${window.location.origin}/pay/${token}/success`;
      const safePhone =
        billingPhone && String(billingPhone).replace(/\D/g, "").length >= 10
          ? billingPhone
          : "+12125550100";

      const billingDetails = {
        name: billingName || "Holy Spirit Church",
        email: billingEmail || "noreply@holyspiritchurchny.org",
        phone: safePhone,
        address: {
          line1: "1919 University Ave",
          city: "Bronx",
          state: "NY",
          postal_code: "10453",
          country: "US",
        },
      };

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || t("_payment_checkout_error"));
        setSubmitting(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        try {
          await axiosInstance.post(`/public/sacrament-payments/${token}/confirm`);
        } catch {
          // Webhook may still finalize; success page polls status.
        }
        history.push(`/pay/${token}/success?payment_intent=${paymentIntent.id}`);
        return;
      }

      onError(t("_payment_checkout_error"));
      setSubmitting(false);
    } catch (err) {
      onError(
        err?.response?.data?.message ||
          t("_payment_checkout_error") ||
          "Could not complete payment."
      );
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          marginTop: 8,
          marginBottom: 20,
          padding: "14px 16px",
          border: "1px solid #e8ecf2",
          borderRadius: 12,
          background: "#fafbfd",
        }}
      >
        <PaymentElement
          options={{
            layout: "tabs",
            paymentMethodOrder: ["card"],
            wallets: {
              applePay: "never",
              googlePay: "never",
              link: "never",
            },
            fields: {
              billingDetails: {
                name: "never",
                email: "never",
                phone: "never",
                address: "never",
              },
            },
            terms: {
              card: "never",
            },
          }}
        />
      </div>
      <div style={{ textAlign: "center" }}>
        <button
          type="submit"
          className="btn"
          disabled={!stripe || !elements || submitting}
          style={{
            background: ACCENT,
            color: "#fff",
            border: "none",
            padding: "12px 36px",
            borderRadius: 8,
            fontWeight: 700,
            fontSize: "1.05rem",
            minWidth: 200,
            opacity: !stripe || submitting ? 0.7 : 1,
          }}
        >
          {submitting
            ? t("_payment_processing") || "Processing..."
            : t("_pay_now")}
        </button>
      </div>
    </form>
  );
};

const NasPaymentPageContent = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPayment = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get(`/public/sacrament-payments/${token}`);
        if (!cancelled) {
          setPayment(res.data?.data ?? res.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              t("_payment_not_found") ||
              "Payment link not found."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (token) fetchPayment();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  useEffect(() => {
    let cancelled = false;

    const createIntent = async () => {
      if (!token || payment?.status !== "pending") return;
      if (!publishableKey) {
        setError(
          t("_payment_checkout_error") ||
            "Stripe publishable key is not configured."
        );
        return;
      }

      setIntentLoading(true);
      try {
        const res = await axiosInstance.post(
          `/public/sacrament-payments/${token}/checkout`
        );
        if (cancelled) return;
        setClientSecret(res.data?.client_secret || null);
        if (!res.data?.client_secret) {
          setError(t("_payment_checkout_error") || "Could not start checkout.");
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              t("_payment_checkout_error") ||
              "Could not start checkout."
          );
        }
      } finally {
        if (!cancelled) setIntentLoading(false);
      }
    };

    createIntent();
    return () => {
      cancelled = true;
    };
  }, [token, payment?.status, t]);

  const stripePromise = useMemo(() => {
    if (!publishableKey) return null;
    return loadStripe(publishableKey);
  }, []);

  const formatMoney = (amount, currency = "usd") => {
    try {
      return new Intl.NumberFormat(i18n.language === "es" ? "es-US" : "en-US", {
        style: "currency",
        currency: (currency || "usd").toUpperCase(),
      }).format(Number(amount || 0));
    } catch {
      return `$${Number(amount || 0).toFixed(2)}`;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(
        i18n.language === "es" ? "es-US" : "en-US",
        { year: "numeric", month: "long", day: "numeric" }
      );
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <section className="section">
        <div className="container text-center" style={{ padding: "60px 0" }}>
          <div className="spinner-border" style={{ color: BRAND }} role="status">
            <span className="sr-only">{t("_loading") || "Loading..."}</span>
          </div>
        </div>
      </section>
    );
  }

  if (error && !payment) {
    return (
      <section className="section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 text-center">
              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  border: "1px solid #eee",
                  padding: "2.5rem 2rem",
                }}
              >
                <i
                  className="fal fa-exclamation-circle"
                  style={{ fontSize: 42, color: ACCENT, marginBottom: 16 }}
                />
                <h4 style={{ color: BRAND, fontWeight: 700 }}>{t("_payment_unavailable")}</h4>
                <p style={{ color: "#666" }}>{error}</p>
                <Link to="/" className="btn" style={{ background: BRAND, color: "#fff" }}>
                  {t("_home")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const style = statusStyles[payment?.status] || statusStyles.pending;
  const canPay = payment?.status === "pending";

  return (
    <section className="section" style={{ background: "#f7f8fa", paddingTop: 40, paddingBottom: 80 }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                boxShadow: "0 10px 40px rgba(3,44,101,0.08)",
                overflow: "hidden",
              }}
            >
              <div style={{ background: BRAND, color: "#fff", padding: "1.75rem 2rem" }}>
                <h3 style={{ margin: 0, fontWeight: 800, color: "#fff" }}>
                  {t("_payment_summary")}
                </h3>
                <p style={{ margin: "8px 0 0", opacity: 0.85 }}>
                  {t("_payment_summary_desc")}
                </p>
              </div>

              <div style={{ padding: "1.75rem 2rem" }}>
                <div
                  style={{
                    display: "inline-block",
                    padding: "6px 14px",
                    borderRadius: 999,
                    background: style.bg,
                    color: style.color,
                    border: `1px solid ${style.border}`,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    marginBottom: 20,
                  }}
                >
                  {t(`_payment_status_${payment.status}`) || payment.status}
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px 0",
                            borderBottom: "2px solid #eee",
                            color: "#555",
                            fontSize: 13,
                          }}
                        >
                          {t("_payment_concept")}
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "10px 0",
                            borderBottom: "2px solid #eee",
                            color: "#555",
                            fontSize: 13,
                          }}
                        >
                          {t("_payment_amount")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(payment.items || []).map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "12px 0",
                              borderBottom: "1px solid #f0f0f0",
                              color: "#333",
                            }}
                          >
                            {item.description}
                          </td>
                          <td
                            style={{
                              padding: "12px 0",
                              borderBottom: "1px solid #f0f0f0",
                              textAlign: "right",
                              fontWeight: 600,
                              color: BRAND,
                            }}
                          >
                            {formatMoney(item.amount, payment.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td
                          style={{
                            paddingTop: 16,
                            fontWeight: 800,
                            color: BRAND,
                            fontSize: "1.05rem",
                          }}
                        >
                          {t("_payment_total")}
                        </td>
                        <td
                          style={{
                            paddingTop: 16,
                            textAlign: "right",
                            fontWeight: 800,
                            color: BRAND,
                            fontSize: "1.25rem",
                          }}
                        >
                          {formatMoney(payment.amount_total, payment.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {payment.expires_at && canPay && (
                  <p style={{ marginTop: 16, color: "#777", fontSize: 14 }}>
                    {t("_payment_expires_on")}: <strong>{formatDate(payment.expires_at)}</strong>
                  </p>
                )}

                {error && (
                  <div
                    className="alert"
                    style={{
                      marginTop: 16,
                      background: "#fdecea",
                      border: "1px solid #f0b4b4",
                      color: "#8a1f1f",
                      borderRadius: 8,
                      padding: "10px 14px",
                    }}
                  >
                    {error}
                  </div>
                )}

                <div style={{ marginTop: 28 }}>
                  {canPay ? (
                    intentLoading || !clientSecret || !stripePromise ? (
                      <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <div className="spinner-border" style={{ color: BRAND }} role="status">
                          <span className="sr-only">{t("_loading") || "Loading..."}</span>
                        </div>
                        <p style={{ marginTop: 12, color: "#777", fontSize: 14 }}>
                          {t("_payment_preparing") || "Preparing secure card form..."}
                        </p>
                      </div>
                    ) : (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                          appearance: {
                            theme: "stripe",
                            variables: {
                              colorPrimary: BRAND,
                              borderRadius: "8px",
                            },
                          },
                        }}
                      >
                        <PaymentForm
                          token={token}
                          t={t}
                          onError={setError}
                          billingName={payment.payer_name}
                          billingEmail={payment.payer_email}
                          billingPhone={payment.payer_phone}
                        />
                      </Elements>
                    )
                  ) : payment.status === "paid" ? (
                    <div style={{ textAlign: "center" }}>
                      <p style={{ color: "#1b6b3a", fontWeight: 600, marginBottom: 12 }}>
                        {t("_payment_already_paid")}
                      </p>
                      <Link
                        to={`/pay/${token}/success`}
                        className="btn"
                        style={{ background: BRAND, color: "#fff" }}
                      >
                        {t("_view_confirmation")}
                      </Link>
                    </div>
                  ) : (
                    <p style={{ color: "#8a1f1f", fontWeight: 600, marginBottom: 0, textAlign: "center" }}>
                      {t("_payment_link_inactive")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NasPaymentPageContent;
